import axios, { AxiosRequestConfig, AxiosInstance } from "axios";
import { ResponseWrapper } from "@/services/response_wrapper";
import { ErrorWrapper } from "@/services/error_wrapper";
import { getPathAPI } from "@/helpers";
const { version } = require("../../package.json");

export const getAuthorizationBearerStringFor = (tokenKey: string) => {
  const token = localStorage.getItem(tokenKey);

  // Validate token before creating Bearer string to prevent "Invalid Token" errors
  if (
    !token ||
    token === "null" ||
    token === "undefined" ||
    token.trim() === ""
  ) {
    throw new Error(`Invalid or missing token for key: ${tokenKey}`);
  }

  return `Bearer ${token}`;
};

const DB_URL = getPathAPI();

interface RequestOptions {
  useToken?: string;
  contentType?: string;
  retry?: boolean;
  maxRetries?: number;
  retryDelay?: number;
}

export default class BaseService {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: DB_URL,
      timeout: 60000 // 30 second timeout
    });
  }

  async request(
    axiosConfig: AxiosRequestConfig,
    options?: RequestOptions
  ): Promise<ResponseWrapper> {
    const requestOptions: RequestOptions = {
      retry: true,
      maxRetries: 2,
      retryDelay: 1000,
      ...options
    };

    return this.executeWithRetry(axiosConfig, requestOptions, 0);
  }

  private async executeWithRetry(
    axiosConfig: AxiosRequestConfig,
    options: RequestOptions,
    attempt: number
  ): Promise<ResponseWrapper> {
    try {
      if (options.useToken) {
        try {
          this.instance.defaults.headers[
            "Authorization"
          ] = getAuthorizationBearerStringFor(options.useToken);
        } catch (tokenError) {
          // If token is invalid, create an AxiosError-like object for consistent error handling
          const error: any = new Error(`Invalid token: ${tokenError.message}`);
          error.response = {
            status: 401,
            data: {
              response: {
                success: false,
                message: "Invalid Token"
              }
            }
          };
          error.config = axiosConfig;
          throw error;
        }
      }
      if (options.contentType) {
        this.instance.defaults.headers["Content-Type"] = options.contentType;
      }
      //default header
      this.instance.defaults.headers["X-Atlas-App-Version"] = version;

      const response = await this.instance.request(axiosConfig);
      return new ResponseWrapper(response);
    } catch (e) {
      const errorWrapper = new ErrorWrapper(e);

      // Determine if we should retry
      const shouldRetry = this.shouldRetryRequest(
        errorWrapper,
        options,
        attempt
      );

      if (shouldRetry) {
        // Wait before retrying with exponential backoff
        const delay = options.retryDelay! * Math.pow(2, attempt);
        await this.delay(delay);

        return this.executeWithRetry(axiosConfig, options, attempt + 1);
      }

      throw errorWrapper;
    }
  }

  private shouldRetryRequest(
    error: ErrorWrapper,
    options: RequestOptions,
    attempt: number
  ): boolean {
    // Don't retry if retry is disabled or we've exceeded max retries
    if (!options.retry || attempt >= (options.maxRetries || 2)) {
      return false;
    }

    // Retry on network errors and timeouts
    if (error.isNetworkError || error.isTimeout) {
      return true;
    }

    // Retry on specific HTTP status codes
    if (typeof error.status === "number") {
      const retryableStatusCodes = [408, 429, 502, 503, 504];
      return retryableStatusCodes.includes(error.status);
    }

    return false;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  getInstance(): AxiosInstance {
    return this.instance;
  }
}
